<script setup lang="ts">
import { useUsersProfile } from '@/composables/core/users'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IUpdateUserProfileForm } from '@/types/core/forms'
import { Icon } from '@iconify/vue'
import { useDark } from '@vueuse/core'
import { ClickOutside as vClickOutside } from 'element-plus'
import { storeToRefs } from 'pinia'
import { ref, unref, watch } from 'vue'
import { useRouter } from 'vue-router'

defineProps<{ isCollapse: boolean }>()

/** Vue Router instance. */
const router = useRouter()

/** Theme state. */
const isDark = useDark()

// User info
const oidcStore = useOIDCStore()
const { profile, isCustomer } = storeToRefs(oidcStore)
const emailNotificationsEnabled = ref<boolean>(profile.value?.email_notifications ?? false)

// Add watch for changes in profile
watch(
  () => profile.value,
  (newProfile) => {
    if (newProfile) {
      emailNotificationsEnabled.value = newProfile.email_notifications
    }
  }
)

// Mutations
const { updateProfileMutation } = useUsersProfile()

const handleEmailNotificationsChange = (val: boolean | string | number) => {
  const form: IUpdateUserProfileForm = {
    email_notifications: val as boolean,
  }

  updateProfileMutation.mutate(form)
}

// Profile
const profileRef = ref()
const popoverRef = ref()

/**
 * Hide profile pop-over on click outside.
 */
const onProfileClickOutside = () => {
  unref(popoverRef).popperRef?.delayHide?.()
}

const handleTeamNavigation = () => {
  router.push({ name: 'teams' })
}
</script>

<template>
  <div ref="profileRef" v-click-outside="onProfileClickOutside" class="mini-profile">
    <el-avatar style="display: flex; justify-content: center; align-items: center" :size="36"
      ><p>{{ profile?.first_name?.charAt(0) }}{{ profile?.last_name?.charAt(0) }}</p>
    </el-avatar>
    <p v-if="!isCollapse">
      {{ oidcStore.getFullName() }}
    </p>
  </div>
  <el-popover
    ref="popoverRef"
    :virtual-ref="profileRef"
    trigger="click"
    virtual-triggering
    placement="right-end"
    width="fit-content"
  >
    <div class="mini-profile__pop-over">
      {{ profile?.email }}
      <el-button v-if="isCustomer" type="primary" size="small" @click="handleTeamNavigation">My team</el-button>
      <el-row :gutter="95">
        <el-col :span="15">
          <small>Dark Mode</small>
        </el-col>
        <el-col :span="5">
          <el-switch v-model="isDark" size="small">
            <template #inactive-action><Icon icon="material-symbols:light-mode-outline-rounded" /></template>
            <template #active-action><Icon icon="material-symbols:dark-mode-outline-rounded" /> </template>
          </el-switch>
        </el-col>
      </el-row>
      <el-row :gutter="95">
        <el-col :span="15">
          <small>Email Notifications</small>
        </el-col>
        <el-col :span="5">
          <el-switch v-model="emailNotificationsEnabled" size="small" @change="handleEmailNotificationsChange">
            <template #inactive-action><Icon icon="material-symbols:mail-outline-rounded" /></template>
            <template #active-action><Icon icon="material-symbols:mail-outline-rounded" /> </template>
          </el-switch>
        </el-col>
      </el-row>
      <el-button size="small" @click="() => oidcStore.logout()">Log out</el-button>
    </div>
  </el-popover>
</template>

<style scoped lang="scss">
.mini-profile {
  user-select: none;
  cursor: pointer;
  display: inline-flex;
  gap: var(--spacing-spacing-sm);
  justify-content: center;
  align-items: center;

  &__pop-over {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-xs);
  }
}
</style>
